import React from 'react';

let bodyObserver = null;
let documentCaptureForRdtBridge = false;

function isCalendarDayDisabled(td) {
  return td.classList && td.classList.contains('rdtDisabled');
}

/** All day cells in this month grid (6×7), in DOM / visual reading order. */
function getCalendarDayCells(table) {
  return Array.from(table.querySelectorAll('tbody td.rdtDay'));
}

/**
 * Next enabled cell when moving along a row (step ±1), skipping disabled days.
 */
function findNextEnabledHorizontal(all, fromIdx, direction) {
  const step = direction > 0 ? 1 : -1;
  let j = fromIdx + step;
  while (j >= 0 && j < all.length) {
    if (!isCalendarDayDisabled(all[j])) return j;
    j += step;
  }
  return -1;
}

/**
 * Next enabled cell in the same weekday column (step ±7), skipping whole disabled rows.
 */
function findNextEnabledVertical(all, fromIdx, direction) {
  const step = direction > 0 ? 7 : -7;
  let j = fromIdx + step;
  while (j >= 0 && j < all.length) {
    if (!isCalendarDayDisabled(all[j])) return j;
    j += step;
  }
  return -1;
}

function findHomeInRow(all, idx) {
  const rowStart = Math.floor(idx / 7) * 7;
  for (let j = rowStart; j < rowStart + 7; j += 1) {
    if (!isCalendarDayDisabled(all[j])) return j;
  }
  return -1;
}

function findEndInRow(all, idx) {
  const rowStart = Math.floor(idx / 7) * 7;
  for (let j = rowStart + 6; j >= rowStart; j -= 1) {
    if (!isCalendarDayDisabled(all[j])) return j;
  }
  return -1;
}

/**
 * Move focus between react-datetime day cells (spatial 7-column grid).
 * @returns {boolean} true if the key was handled (caller should not run other handlers)
 */
function handleCalendarDayGridNavigation(e, currentTd) {
  const navigationKeys = [
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
  ];
  if (!navigationKeys.includes(e.key)) {
    return false;
  }

  const table = currentTd.closest('table');
  if (!table) return false;

  const all = getCalendarDayCells(table);
  const idx = all.indexOf(currentTd);
  if (idx === -1) return false;

  let nextIdx = -1;
  if (e.key === 'ArrowRight') {
    nextIdx = findNextEnabledHorizontal(all, idx, 1);
  } else if (e.key === 'ArrowLeft') {
    nextIdx = findNextEnabledHorizontal(all, idx, -1);
  } else if (e.key === 'ArrowDown') {
    nextIdx = findNextEnabledVertical(all, idx, 1);
  } else if (e.key === 'ArrowUp') {
    nextIdx = findNextEnabledVertical(all, idx, -1);
  } else if (e.key === 'Home') {
    nextIdx = findHomeInRow(all, idx);
  } else if (e.key === 'End') {
    nextIdx = findEndInRow(all, idx);
  }

  if (nextIdx >= 0 && nextIdx < all.length && nextIdx !== idx) {
    e.preventDefault();
    e.stopPropagation();
    all[nextIdx].focus();
    return true;
  }

  // First column: ArrowLeft moves to "Previous month" (focus target is button.bf-rdt-month-nav when enhanced).
  if (e.key === 'ArrowLeft' && nextIdx < 0 && idx % 7 === 0) {
    const prevTh = table.querySelector('thead th.rdtPrev');
    const prevTarget = getMonthNavFocusTarget(prevTh);
    if (prevTarget && prevTarget.getAttribute('tabindex') !== '-1') {
      e.preventDefault();
      e.stopPropagation();
      prevTarget.focus({ preventScroll: true });
      return true;
    }
  }

  // Last column: ArrowRight moves to "Next month".
  if (e.key === 'ArrowRight' && nextIdx < 0 && idx % 7 === 6) {
    const nextTh = table.querySelector('thead th.rdtNext');
    const nextTarget = getMonthNavFocusTarget(nextTh);
    if (nextTarget && nextTarget.getAttribute('tabindex') !== '-1') {
      e.preventDefault();
      e.stopPropagation();
      nextTarget.focus({ preventScroll: true });
      return true;
    }
  }

  // Top row: ArrowUp leaves the grid back to the text field (picker is open but focus was on a day)
  if (e.key === 'ArrowUp' && idx >= 0 && idx < 7 && nextIdx < 0) {
    const rdtRoot = table.closest('.rdt');
    const dateInput = rdtRoot && rdtRoot.querySelector('input');
    if (dateInput) {
      e.preventDefault();
      e.stopPropagation();
      dateInput.focus();
      return true;
    }
  }

  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
    e.preventDefault();
    return true;
  }
  return false;
}

/**
 * react-datetime renderDay: expose selected / today / disabled state for assistive tech.
 * Uses role="gridcell" with aria-selected; pair with ensureReactDatetimeA11yEnhancements() so the
 * parent table has role="grid".
 */
export function renderAccessibleDatetimeDay(props, currentDate) {
  const { key, onKeyDown: existingOnKeyDown, ...rest } = props;
  const className = rest.className || '';
  const isSelected = /\brdtActive\b/.test(className);
  const isToday = /\brdtToday\b/.test(className);
  const isDisabled = /\brdtDisabled\b/.test(className);
  const dayLabel = currentDate && typeof currentDate.format === 'function'
    ? currentDate.format('LL')
    : '';

  return (
    <td
      key={key}
      {...rest}
      aria-current={isToday ? 'date' : undefined}
      aria-disabled={isDisabled ? true : undefined}
      aria-label={dayLabel || undefined}
      aria-selected={isSelected ? 'true' : 'false'}
      role="gridcell"
      tabIndex={isDisabled ? -1 : 0}
      onKeyDown={(e) => {
        if (typeof existingOnKeyDown === 'function') {
          existingOnKeyDown(e);
        }
        if (isDisabled) return;

        if (handleCalendarDayGridNavigation(e, e.currentTarget)) {
          return;
        }

        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.click();
        }
      }}
    >
      {currentDate.date()}
    </td>
  );
}

function focusFirstRowRightmostEnabledDay(table) {
  const all = getCalendarDayCells(table);
  for (let col = 6; col >= 0; col -= 1) {
    const td = all[col];
    if (td && !isCalendarDayDisabled(td)) {
      td.focus({ preventScroll: true });
      return true;
    }
  }
  return false;
}

const MONTH_NAV_BTN_CLASS = 'bf-rdt-month-nav';

/** Prefer native button (reliable focus) over span/th inside role="grid". */
function getOrCreateMonthNavButton(th) {
  if (!th) return null;
  let btn = th.querySelector(`:scope > button.${MONTH_NAV_BTN_CLASS}`);
  if (btn) return btn;
  btn = document.createElement('button');
  btn.type = 'button';
  btn.className = MONTH_NAV_BTN_CLASS;
  while (th.firstChild) {
    btn.appendChild(th.firstChild);
  }
  th.appendChild(btn);
  return btn;
}

function getMonthNavFocusTarget(th) {
  if (!th) return null;
  return th.querySelector(`:scope > button.${MONTH_NAV_BTN_CLASS}`) || th.querySelector(':scope > span') || th;
}

function wireMonthNavControlKeyboard(controlEl, hostTh) {
  if (!controlEl || !hostTh || controlEl.getAttribute('data-bf-rdt-nav-keys') === 'true') {
    return;
  }
  controlEl.setAttribute('data-bf-rdt-nav-keys', 'true');
  const isPrev = hostTh.classList.contains('rdtPrev');
  const isNext = hostTh.classList.contains('rdtNext');

  controlEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      hostTh.click();
      return;
    }

    const table = hostTh.closest('table');
    const picker = hostTh.closest('.rdtPicker');
    if (!table || !picker) return;

    if (isPrev && (e.key === 'ArrowRight' || e.key === 'ArrowDown')) {
      const day = findPreferredFocusDayCell(picker);
      if (day) {
        e.preventDefault();
        e.stopPropagation();
        day.focus({ preventScroll: true });
      }
      return;
    }

    if (isNext && (e.key === 'ArrowLeft' || e.key === 'ArrowDown')) {
      if (focusFirstRowRightmostEnabledDay(table)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const day = findPreferredFocusDayCell(picker);
      if (day) {
        e.preventDefault();
        e.stopPropagation();
        day.focus({ preventScroll: true });
      }
    }
  });
}

/**
 * Wrap month chevrons in a native <button> (reliable focus) and keep th out of the tab order.
 * Re-runs safely after React replaces header nodes (no early return on v2 alone).
 */
function enhanceMonthNavTh(th, ariaLabel) {
  if (!th) return;

  const btn = getOrCreateMonthNavButton(th);
  if (!btn) return;

  th.removeAttribute('role');
  th.removeAttribute('aria-label');
  th.removeAttribute('data-bf-rdt-nav-keys');
  th.setAttribute('tabindex', '-1');

  btn.setAttribute('type', 'button');
  btn.setAttribute('aria-label', ariaLabel);
  btn.setAttribute('tabindex', '0');

  if (btn.getAttribute('data-bf-rdt-nav-keys') !== 'true') {
    wireMonthNavControlKeyboard(btn, th);
  }
  th.setAttribute('data-bf-rdt-nav-a11y', 'v2');
}

function enhanceNavButtons(picker) {
  enhanceMonthNavTh(picker.querySelector('.rdtPrev'), 'Previous month');
  enhanceMonthNavTh(picker.querySelector('.rdtNext'), 'Next month');
}

function syncCalendarGridRoles(picker) {
  const table = picker.querySelector('.rdtDays table');
  if (!table || table.getAttribute('data-bf-rdt-grid-synced') === 'true') {
    return;
  }

  table.setAttribute('data-bf-rdt-grid-synced', 'true');
  table.setAttribute('role', 'grid');
  table.querySelectorAll('tr').forEach((tr) => {
    tr.setAttribute('role', 'row');
  });
  table.querySelectorAll('thead th').forEach((th) => {
    if (th.classList.contains('rdtPrev') || th.classList.contains('rdtNext')) {
      return;
    }
    th.setAttribute('role', 'columnheader');
  });
}

function findPreferredFocusDayCell(picker) {
  const table = picker.querySelector('.rdtDays table');
  if (!table) return null;
  const active = table.querySelector('tbody td.rdtDay.rdtActive:not(.rdtDisabled)');
  if (active) return active;
  const today = table.querySelector('tbody td.rdtDay.rdtToday:not(.rdtDisabled)');
  if (today) return today;
  const all = getCalendarDayCells(table);
  return all.find((td) => !isCalendarDayDisabled(td)) || null;
}

/**
 * Move keyboard focus from a react-datetime text field into the day grid (APG datepicker pattern).
 * Used from inputProps.onKeyDown and from a document capture listener so it still runs when
 * props merging or focus timing would otherwise skip the handler.
 *
 * @returns {boolean} true if the key event was handled (caller may preventDefault)
 */
export function bridgeDateInputKeydownToCalendarIfApplicable(target, e) {
  if (e.defaultPrevented) return false;
  if (e.key !== 'ArrowDown' && e.key !== 'F4' && !(e.altKey && e.key === 'ArrowDown')) return false;

  const input = target;
  if (!(input instanceof window.HTMLInputElement) || input.disabled) return false;

  const rdt = input.closest('.rdt');
  if (!rdt) return false;

  const isOpen = rdt.classList.contains('rdtOpen') || rdt.classList.contains('rdtStatic');
  if (!isOpen) return false;

  const picker = rdt.querySelector('.rdtPicker');
  if (!picker) return false;

  const dayEl = findPreferredFocusDayCell(picker);
  if (!dayEl) return false;

  e.preventDefault();
  const focusDay = () => {
    dayEl.focus({ preventScroll: true });
    if (document.activeElement !== dayEl) {
      window.requestAnimationFrame(() => {
        dayEl.focus({ preventScroll: true });
      });
    }
  };
  // Defer past react commit / modal focus so the cell can actually receive focus
  window.setTimeout(focusDay, 0);
  return true;
}

function bindDocumentCaptureForRdtDateInputBridge() {
  if (documentCaptureForRdtBridge || typeof document === 'undefined') {
    return;
  }
  documentCaptureForRdtBridge = true;
  document.addEventListener(
    'keydown',
    (ev) => {
      bridgeDateInputKeydownToCalendarIfApplicable(ev.target, ev);
    },
    true
  );
}

/**
 * Pass via react-datetime inputProps.onKeyDown (v2.16 chains this before onInputKey).
 * Document-level capture is also registered in ensureReactDatetimeA11yEnhancements().
 */
export function handleDateInputArrowToCalendarKeydown(e) {
  bridgeDateInputKeydownToCalendarIfApplicable(e.currentTarget, e);
}

function enhanceAllReactDatetimePickers() {
  document.querySelectorAll('.rdtPicker').forEach((picker) => {
    syncCalendarGridRoles(picker);
    enhanceNavButtons(picker);
  });
}

/**
 * Idempotent: wires MutationObserver once so every react-datetime calendar (any route) gets
 * grid roles on the table and nav button labels.
 */
export function ensureReactDatetimeA11yEnhancements() {
  if (typeof document === 'undefined') {
    return;
  }

  bindDocumentCaptureForRdtDateInputBridge();

  if (bodyObserver) {
    return;
  }

  enhanceAllReactDatetimePickers();

  bodyObserver = new MutationObserver(() => {
    enhanceAllReactDatetimePickers();
  });

  bodyObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
